class RitualFashionBogo extends HTMLElement {
  connectedCallback() {
    this.units = [...this.querySelectorAll('[data-rfb-unit]')];
    this.variantCache = new WeakMap();
    this.zoomImageCache = new Map();
    this.zoomRequestToken = 0;

    this.querySelectorAll('[data-rfb-source-choice]').forEach((button) => button.addEventListener('click', () => this.chooseSource(button)));
    this.querySelectorAll('[data-rfb-choice]').forEach((button) => button.addEventListener('click', () => this.choose(button)));
    this.querySelectorAll('[data-rfb-size-tab]').forEach((button) => button.addEventListener('click', () => this.switchSizeSystem(button)));
    this.setupSizeSystems();
    this.querySelector('[data-rfb-submit]')?.addEventListener('click', (event) => this.submit(event));
    this.querySelectorAll('[data-rfb-open-guide]').forEach((button) => button.addEventListener('click', () => this.openGuide()));
    this.querySelectorAll('[data-rfb-close-guide]').forEach((button) => button.addEventListener('click', () => this.closeGuide()));
    this.querySelector('[data-rfb-guide]')?.addEventListener('click', (event) => {
      if (event.target === event.currentTarget) this.closeGuide();
    });
    this.querySelectorAll('[data-rfb-open-zoom]').forEach((button) => button.addEventListener('click', () => this.openZoom(button)));
    this.querySelector('[data-rfb-close-zoom]')?.addEventListener('click', () => this.closeZoom());
    this.querySelector('[data-rfb-zoom-dialog]')?.addEventListener('click', (event) => {
      if (event.target === event.currentTarget) this.closeZoom();
    });
    this.querySelector('[data-rfb-zoom-stage]')?.addEventListener('click', (event) => {
      if (event.target === event.currentTarget) this.closeZoom();
    });
    this.querySelector('[data-rfb-zoom-dialog]')?.addEventListener('close', () => {
      document.documentElement.classList.remove('rfb-zoom-open');
    });

    this.units.forEach((unit) => {
      unit._rfbPriority = [];
      unit._rfbSourceState = new Map();
      this.normalizeSourceChoices(unit);
      this.setActiveSource(unit, unit.dataset.rfbActiveSource || '1');
      this.normalizeUnit(unit);
      this.resolveUnit(unit, false);
    });

    this.setupScrollCues();
    this.startTimer();
    this.startStock();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    clearInterval(this.stockTimer);
    this.scrollCueItems?.forEach((wrap) => {
      wrap.querySelector('.rfb__values')?.removeEventListener('scroll', this.updateScrollCues);
    });
    if (this.updateScrollCues) window.removeEventListener('resize', this.updateScrollCues);
    document.documentElement.classList.remove('rfb-zoom-open');
  }

  activeSource(unit) {
    return String(unit?.dataset.rfbActiveSource || '1');
  }

  sourcePanel(unit, source = this.activeSource(unit)) {
    return unit?.querySelector(`[data-rfb-source-panel="${source}"]`) || unit || null;
  }

  sourceMeta(unit, source = this.activeSource(unit)) {
    const panel = this.sourcePanel(unit, source);
    return {
      panel,
      productId: panel?.dataset.rfbProductId || unit?.dataset.rfbProductId || '',
      productTitle: panel?.dataset.rfbProductTitle || unit?.dataset.rfbProductTitle || '',
      productThumb: panel?.dataset.rfbProductThumb || '',
      productImage: panel?.dataset.rfbProductImage || '',
      productImageAlt: panel?.dataset.rfbProductImageAlt || panel?.dataset.rfbProductTitle || '',
      defaultVariant: (panel?.dataset.rfbDefaultVariant || unit?.dataset.rfbDefaultVariant) === 'true',
      visualPositions: String(panel?.dataset.rfbVisualPositions || '').split(',').map((value) => Number(value)).filter((value) => value > 0),
    };
  }

  setActiveSource(unit, source) {
    if (!unit) return;
    const next = String(source || '1');
    unit.dataset.rfbActiveSource = next;
    unit.querySelectorAll('[data-rfb-source-panel]').forEach((panel) => {
      panel.hidden = String(panel.dataset.rfbSourcePanel) !== next;
    });
  }

  groups(unit) {
    const panel = this.sourcePanel(unit);
    return [...(panel?.querySelectorAll('[data-rfb-option]') || [])];
  }

  variantsFor(unit, source = this.activeSource(unit)) {
    if (!unit) return [];
    let cache = this.variantCache?.get(unit);
    if (!cache) {
      cache = new Map();
      this.variantCache?.set(unit, cache);
    }
    const key = String(source || '1');
    if (cache.has(key)) return cache.get(key);
    let variants = [];
    const panel = this.sourcePanel(unit, key);
    try {
      variants = JSON.parse(panel?.querySelector(`[data-rfb-source-variants="${key}"]`)?.textContent || unit.querySelector('[data-rfb-unit-variants]')?.textContent || '[]');
    } catch (_) {}
    cache.set(key, variants);
    return variants;
  }

  setupSizeSystems() {
    this.querySelectorAll('[data-rfb-size-system]').forEach((group) => {
      const tabs = [...group.querySelectorAll('[data-rfb-size-tab]')];
      const choices = [...group.querySelectorAll('[data-rfb-choice]')];
      if (!tabs.length || !choices.length) return;
      tabs.forEach((tab) => {
        const prefix = String(tab.dataset.sizePrefix || '').trim();
        const has = Boolean(prefix) && choices.some((choice) => this.sizePrefixMatches(choice.dataset.value || '', prefix));
        tab.hidden = !has;
      });
      const usable = tabs.filter((tab) => !tab.hidden);
      if (!usable.length) {
        group.dataset.rfbSizeReady = 'false';
        tabs.forEach((tab) => { tab.hidden = true; });
        return;
      }
      group.dataset.rfbSizeReady = 'true';
      choices.forEach((choice) => {
        const raw = choice.dataset.value || '';
        const tab = usable.find((item) => this.sizePrefixMatches(raw, item.dataset.sizePrefix || ''));
        choice.dataset.sizeGroup = tab?.dataset.sizeGroup || '';
        const label = choice.querySelector('span');
        if (label && tab) label.textContent = this.stripSizePrefix(raw, tab.dataset.sizePrefix || '');
      });
      const target = this.defaultSizeTab(group, usable);
      group.dataset.rfbSizeActive = target?.dataset.sizeGroup || usable[0].dataset.sizeGroup || '';
      this.paintSizeTabs(group);
    });
  }

  sizePrefixPattern(prefix) {
    const compact = String(prefix || '').trim().toLocaleLowerCase().replace(/[\s_\-–—:./]+/g, '');
    if (!compact) return null;
    const escaped = [...compact].map((char) => char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('[\\s_\\-–—:./]*');
    return new RegExp(`^${escaped}(?:[\\s_\\-–—:./]+|(?=\\d))`, 'iu');
  }

  sizePrefixMatches(value, prefix) {
    const pattern = this.sizePrefixPattern(prefix);
    return Boolean(pattern) && pattern.test(String(value || '').trim());
  }

  stripSizePrefix(value, prefix) {
    const raw = String(value || '').trim();
    const pattern = this.sizePrefixPattern(prefix);
    if (!pattern) return raw;
    const match = raw.match(pattern);
    return match ? raw.slice(match[0].length).trim() : raw;
  }

  defaultSizeTab(group, tabs) {
    const forced = String(this.dataset.sizeDefault || 'auto');
    if (forced !== 'auto') return tabs.find((tab) => String(tab.dataset.sizeGroup) === forced) || tabs[0];
    const country = String(this.dataset.country || '').toUpperCase();
    const eu = new Set(['AT','BE','BG','HR','CY','CZ','DE','DK','EE','ES','FI','FR','GR','HU','IE','IT','LT','LU','LV','MT','NL','PL','PT','RO','SE','SI','SK','NO','CH','IS','LI','AE','SA','QA','KW','BH','OM']);
    let keys = [];
    if (country === 'GB') keys = ['uk','gb'];
    else if (country === 'US' || country === 'CA') keys = ['us w','usw','women','us','us m','usm'];
    else if (country === 'AU' || country === 'NZ') keys = ['uk','au'];
    else if (eu.has(country)) keys = ['eu','eur'];
    const normalized = (tab) => `${tab.textContent || ''} ${tab.dataset.sizePrefix || ''}`.toLocaleLowerCase().replace(/[^a-z0-9]+/g, ' ');
    for (const key of keys) {
      const found = tabs.find((tab) => normalized(tab).includes(key.replace(/[^a-z0-9]+/g, ' ')));
      if (found) return found;
    }
    const selected = group.querySelector('[data-rfb-choice].is-selected')?.dataset.value || '';
    return tabs.find((tab) => this.sizePrefixMatches(selected, tab.dataset.sizePrefix || '')) || tabs[0];
  }

  paintSizeTabs(group) {
    const active = String(group.dataset.rfbSizeActive || '');
    group.querySelectorAll('[data-rfb-size-tab]').forEach((tab) => {
      const selected = String(tab.dataset.sizeGroup) === active;
      tab.classList.toggle('is-selected', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });
  }

  sizeChoiceAllowed(group, choice) {
    if (group?.dataset.rfbSizeReady !== 'true') return true;
    const active = String(group.dataset.rfbSizeActive || '');
    return !choice.dataset.sizeGroup || String(choice.dataset.sizeGroup) === active;
  }

  displayOptionValue(group, value) {
    if (group?.dataset.rfbSizeReady !== 'true') return value || '';
    const tab = [...group.querySelectorAll('[data-rfb-size-tab]')].find((item) => this.sizePrefixMatches(value, item.dataset.sizePrefix || ''));
    return tab ? this.stripSizePrefix(value, tab.dataset.sizePrefix || '') : (value || '');
  }

  switchSizeSystem(tab) {
    const group = tab.closest('[data-rfb-size-system]');
    const unit = tab.closest('[data-rfb-unit]');
    if (!group || !unit || group.dataset.rfbSizeReady !== 'true' || tab.hidden) return;
    group.dataset.rfbSizeActive = String(tab.dataset.sizeGroup || '');
    this.paintSizeTabs(group);
    const groupIndex = this.groups(unit).indexOf(group);
    if (!Array.isArray(unit._rfbPriority)) unit._rfbPriority = [];
    unit._rfbPriority = [groupIndex, ...unit._rfbPriority.filter((index) => index !== groupIndex)];
    this.normalizeUnit(unit);
    this.resolveUnit(unit, unit === this.units[0]);
  }

  normalizeSourceChoices(unit) {
    const buttons = [...(unit?.querySelectorAll('[data-rfb-source-choice]') || [])];
    if (!buttons.length) return;
    const keepers = new Map();
    buttons.forEach((button) => {
      button.hidden = false;
      const key = `${button.dataset.source || '1'}::${button.dataset.visualKey || button.dataset.variantId || ''}`;
      const existing = keepers.get(key);
      if (!existing) {
        keepers.set(key, button);
        return;
      }
      if (existing.disabled && !button.disabled) {
        existing.hidden = true;
        existing.classList.remove('is-selected');
        existing.setAttribute('aria-selected', 'false');
        keepers.set(key, button);
      } else {
        button.hidden = true;
        button.classList.remove('is-selected');
        button.setAttribute('aria-selected', 'false');
      }
    });
  }

  syncSourceChoice(unit, variant) {
    const buttons = [...(unit?.querySelectorAll('[data-rfb-source-choice]:not([hidden])') || [])];
    if (!buttons.length) return;
    const source = this.activeSource(unit);
    // In a multi-product gift each button represents one PRODUCT, not one variant.
    // This prevents Color/Material/Size schemas from different products being flattened together.
    const selected = buttons.find((button) => String(button.dataset.source) === source);
    buttons.forEach((button) => {
      const active = button === selected;
      button.classList.toggle('is-selected', active);
      button.setAttribute('aria-selected', String(active));
    });
  }

  selections(unit) {
    return this.groups(unit).map((group) => group.querySelector('[data-rfb-choice].is-selected')?.dataset.value || '');
  }

  priority(unit) {
    const groups = this.groups(unit);
    const interacted = Array.isArray(unit._rfbPriority)
      ? unit._rfbPriority.filter((index, position, list) => index >= 0 && index < groups.length && list.indexOf(index) === position)
      : [];
    const remaining = groups.map((_, index) => index).filter((index) => !interacted.includes(index));
    return [...interacted, ...remaining];
  }

  selectChoice(group, choice) {
    group.querySelectorAll('[data-rfb-choice]').forEach((item) => {
      const selected = item === choice;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-pressed', String(selected));
    });
    const label = group.querySelector('[data-rfb-value]');
    if (label) label.textContent = this.displayOptionValue(group, choice?.dataset.value || '');
  }

  variantsMatching(unit, constraints, optionIndex, value) {
    return this.variantsFor(unit).filter((variant) => {
      if (variant.options[optionIndex] !== value) return false;
      return [...constraints.entries()].every(([index, selectedValue]) => !selectedValue || variant.options[index] === selectedValue);
    });
  }

  bestVariant(candidates, selected) {
    return candidates
      .map((variant) => ({
        variant,
        score: (variant.available ? 1000 : 0) + variant.options.reduce((score, value, index) => score + (selected[index] === value ? 1 : 0), 0),
      }))
      .sort((a, b) => b.score - a.score)[0]?.variant;
  }

  updateChoiceImage(choice, variant) {
    const image = choice.querySelector('img');
    if (!image || !variant?.featured_image?.src) return;
    image.src = this.imageUrl(variant.featured_image.src, 360);
    image.srcset = '';
    image.alt = variant.title || choice.dataset.value || '';
  }

  /*
    Variant compatibility is resolved in the order the shopper actually interacts
    with options, not the order Shopify happens to store them. If Material is the
    first choice the shopper changes, it becomes the leading constraint and Color,
    Size, Style, etc. immediately narrow to real combinations for that material.
  */
  normalizeUnit(unit) {
    const groups = this.groups(unit);
    const selected = this.selections(unit);

    /* Before the shopper interacts, keep every globally valid option visible.
       This prevents Shopify option order (for example Color before Material)
       from hiding a legitimate first choice. The first option the shopper
       actually changes becomes the leading compatibility constraint. */
    if (!Array.isArray(unit._rfbPriority) || unit._rfbPriority.length === 0) {
      const noConstraints = new Map();
      groups.forEach((group, optionIndex) => {
        const choices = [...group.querySelectorAll('[data-rfb-choice]')];
        let firstAvailable = null;
        let firstExisting = null;
        choices.forEach((choice) => {
          const candidates = this.variantsMatching(unit, noConstraints, optionIndex, choice.dataset.value);
          const exists = candidates.length > 0;
          const available = candidates.some((variant) => variant.available);
          choice.hidden = !exists || !this.sizeChoiceAllowed(group, choice);
          choice.disabled = !available;
          choice.classList.toggle('is-sold-out', exists && !available);
          choice.setAttribute('aria-disabled', String(!available));
          if (exists && !firstExisting) firstExisting = choice;
          if (available && !firstAvailable) firstAvailable = choice;
          if (exists) this.updateChoiceImage(choice, this.bestVariant(candidates, selected));
        });
        let current = group.querySelector('[data-rfb-choice].is-selected');
        if (!current || current.hidden || current.disabled) current = firstAvailable || firstExisting;
        this.selectChoice(group, current);
        selected[optionIndex] = current?.dataset.value || '';
      });
      requestAnimationFrame(() => this.updateScrollCues?.());
      return;
    }

    const order = this.priority(unit);
    const constraints = new Map();

    order.forEach((optionIndex) => {
      const group = groups[optionIndex];
      const choices = [...group.querySelectorAll('[data-rfb-choice]')];
      let firstAvailable = null;
      let firstExisting = null;

      choices.forEach((choice) => {
        const candidates = this.variantsMatching(unit, constraints, optionIndex, choice.dataset.value);
        const exists = candidates.length > 0;
        const available = candidates.some((variant) => variant.available);

        choice.hidden = !exists || !this.sizeChoiceAllowed(group, choice);
        choice.disabled = !available;
        choice.classList.toggle('is-sold-out', exists && !available);
        choice.setAttribute('aria-disabled', String(!available));

        if (exists && !firstExisting) firstExisting = choice;
        if (available && !firstAvailable) firstAvailable = choice;
      });

      let current = group.querySelector('[data-rfb-choice].is-selected');
      if (!current || current.hidden || current.disabled) current = firstAvailable || firstExisting;
      this.selectChoice(group, current);
      selected[optionIndex] = current?.dataset.value || '';
      if (current) constraints.set(optionIndex, current.dataset.value);
    });

    /* Variant thumbnails follow the combinations that are currently reachable. */
    const imageConstraints = new Map();
    order.forEach((optionIndex) => {
      const group = groups[optionIndex];
      group.querySelectorAll('[data-rfb-choice]').forEach((choice) => {
        if (choice.hidden) return;
        const candidates = this.variantsMatching(unit, imageConstraints, optionIndex, choice.dataset.value);
        this.updateChoiceImage(choice, this.bestVariant(candidates, selected));
      });
      const selectedChoice = group.querySelector('[data-rfb-choice].is-selected');
      if (selectedChoice) imageConstraints.set(optionIndex, selectedChoice.dataset.value);
    });

    requestAnimationFrame(() => this.updateScrollCues?.());
  }

  chooseSource(button) {
    if (button.disabled || button.hidden) return;
    const unit = button.closest('[data-rfb-unit]');
    if (!unit) return;

    const currentSource = this.activeSource(unit);
    const currentId = unit.querySelector('[data-rfb-variant-id]')?.value;
    if (!unit._rfbSourceState) unit._rfbSourceState = new Map();
    if (currentSource && currentId) {
      unit._rfbSourceState.set(currentSource, {
        variantId: String(currentId),
        priority: Array.isArray(unit._rfbPriority) ? [...unit._rfbPriority] : [],
      });
    }

    const source = String(button.dataset.source || '1');
    this.setActiveSource(unit, source);

    // Synchronize the summary preview/zoom source immediately when moving between
    // different complimentary products. resolveUnit() will refine it to the exact
    // selected variant a few lines below, but this prevents the magnifier from
    // disappearing or pointing at the previous product during the transition.
    const sourcePreview = unit.querySelector('[data-rfb-preview]');
    const sourceInfo = this.sourceMeta(unit, source);
    if (sourcePreview) {
      const sourceTrigger = sourcePreview.querySelector('[data-rfb-open-zoom]');
      if (sourceInfo.productImage) {
        sourcePreview.dataset.rfbZoomSrc = sourceInfo.productImage;
        sourcePreview.dataset.rfbZoomAlt = sourceInfo.productImageAlt || sourceInfo.productTitle || '';
        if (sourceTrigger) sourceTrigger.hidden = false;
        this.preloadZoomImage(sourceInfo.productImage);
      } else if (sourceTrigger) {
        sourceTrigger.hidden = true;
      }
    }

    const saved = unit._rfbSourceState.get(source);
    unit._rfbPriority = Array.isArray(saved?.priority) ? [...saved.priority] : [];

    const variants = this.variantsFor(unit, source);
    const variant = variants.find((item) => String(item.id) === String(saved?.variantId))
      || variants.find((item) => String(item.id) === String(button.dataset.variantId) && item.available)
      || variants.find((item) => item.available)
      || variants[0];
    if (!variant) return;

    this.groups(unit).forEach((group, index) => {
      const value = variant.options?.[index];
      const choice = [...group.querySelectorAll('[data-rfb-choice]')].find((item) => item.dataset.value === value);
      if (choice) this.selectChoice(group, choice);
    });

    this.normalizeUnit(unit);
    this.resolveUnit(unit, false);
    this.syncSourceChoice(unit, variant);
    requestAnimationFrame(() => this.updateScrollCues?.());
  }

  choose(button) {
    if (button.disabled || button.hidden) return;
    const unit = button.closest('[data-rfb-unit]');
    const group = button.closest('[data-rfb-option]');
    if (!unit || !group) return;

    const groupIndex = this.groups(unit).indexOf(group);
    if (!Array.isArray(unit._rfbPriority)) unit._rfbPriority = [];
    if (!unit._rfbPriority.includes(groupIndex)) unit._rfbPriority.push(groupIndex);

    this.selectChoice(group, button);
    this.normalizeUnit(unit);
    this.resolveUnit(unit, unit === this.units[0]);
  }

  resolveUnit(unit, updateGallery = true) {
    const options = this.selections(unit);
    const variants = this.variantsFor(unit);
    const variant = variants.find((item) => item.options.every((value, index) => value === options[index]));
    const input = unit.querySelector('[data-rfb-variant-id]');
    const unavailable = unit.querySelector('[data-rfb-availability]');
    const valid = Boolean(variant?.available);

    if (input) input.value = valid ? variant.id : '';
    if (unavailable) unavailable.hidden = valid;
    unit.classList.toggle('is-unavailable', !valid);

    const variantTitle = unit.querySelector('[data-rfb-variant-title]');
    const meta = this.sourceMeta(unit);
    if (variantTitle) {
      const groups = this.groups(unit);
      const cleanOptions = options.map((value, index) => this.displayOptionValue(groups[index], value)).filter(Boolean);
      variantTitle.textContent = meta.defaultVariant ? (meta.productTitle || variant?.title || '') : cleanOptions.join(' / ');
    }

    if (variant) {
      const preview = unit.querySelector('[data-rfb-preview]');
      const rawVariantImage = variant?.featured_image?.src || '';
      const previewSrc = rawVariantImage ? this.imageUrl(rawVariantImage, 420) : meta.productThumb;
      const zoomSrc = rawVariantImage ? this.imageUrl(rawVariantImage, 2400) : meta.productImage;
      const imageAlt = variant.title || meta.productImageAlt || meta.productTitle || '';

      if (preview && previewSrc) {
        let image = preview.querySelector('img');
        if (!image) {
          image = document.createElement('img');
          image.loading = 'lazy';
          const trigger = preview.querySelector('[data-rfb-open-zoom]');
          preview.insertBefore(image, trigger || null);
        }
        image.src = previewSrc;
        image.srcset = '';
        image.alt = imageAlt;
      } else if (preview) {
        // Never leave the previous product's thumbnail behind when a source has no image.
        // This also prevents the HD viewer from being opened against stale visual state.
        preview.querySelector('img')?.remove();
      }
      if (preview) {
        const trigger = preview.querySelector('[data-rfb-open-zoom]');
        if (zoomSrc) {
          preview.dataset.rfbZoomSrc = zoomSrc;
          preview.dataset.rfbZoomAlt = imageAlt;
          if (trigger) trigger.hidden = false;
          this.preloadZoomImage(zoomSrc);
        } else {
          delete preview.dataset.rfbZoomSrc;
          delete preview.dataset.rfbZoomAlt;
          if (trigger) trigger.hidden = true;
        }
      }

      if (!unit._rfbSourceState) unit._rfbSourceState = new Map();
      unit._rfbSourceState.set(this.activeSource(unit), {
        variantId: String(variant.id),
        priority: Array.isArray(unit._rfbPriority) ? [...unit._rfbPriority] : [],
      });

      this.syncSourceChoice(unit, variant);
      const paidUnit = this.units[0];
      const paidId = paidUnit?.querySelector('[data-rfb-variant-id]')?.value;
      const paidVariant = this.variantsFor(paidUnit).find((item) => String(item.id) === String(paidId)) || (unit === paidUnit ? variant : null);
      this.updatePrices(paidVariant);
    }
    if (variant && updateGallery) this.showVariantMedia(variant);
  }

  updatePrices(paidVariant) {
    if (!paidVariant) return;
    const paidPrice = Number(paidVariant.price || 0);
    const selectedGiftVariants = this.units.slice(1).map((unit) => {
      const id = unit.querySelector('[data-rfb-variant-id]')?.value;
      const variants = this.variantsFor(unit);
      return variants.find((item) => String(item.id) === String(id)) || variants.find((item) => item.available) || variants[0] || null;
    }).filter(Boolean);

    const price = this.querySelector('[data-rfb-price]');
    const compare = this.querySelector('[data-rfb-compare]');
    if (price) price.textContent = this.money(paidPrice);
    if (!compare) return;

    const showCompare = this.dataset.showCompare !== 'false';
    const mode = this.dataset.compareMode || 'paid_compare_at';
    const retailValue = (variant) => {
      const current = Number(variant?.price || 0);
      const crossed = Number(variant?.compare_at_price || 0);
      return crossed > current ? crossed : current;
    };

    let reference = 0;
    if (showCompare) {
      if (mode === 'paid_compare_at') {
        const paidCompare = Number(paidVariant.compare_at_price || 0);
        reference = paidCompare > paidPrice ? paidCompare : 0;
      } else if (mode === 'total_compare_at') {
        reference = retailValue(paidVariant) + selectedGiftVariants.reduce((sum, variant) => sum + retailValue(variant), 0);
      } else if (mode === 'total_current') {
        reference = paidPrice + selectedGiftVariants.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
      }
    }

    const meaningfulReference = reference > paidPrice;
    compare.hidden = !meaningfulReference;
    if (meaningfulReference) compare.textContent = this.money(reference);
  }

  money(cents) {
    return new Intl.NumberFormat(this.dataset.moneyLocale || document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  imageUrl(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  showVariantMedia(variant) {
    const mediaId = variant?.featured_media?.id;
    if (!mediaId) return;
    const gallery = (this.closest('.shopify-section') || document).querySelector('media-gallery') || document.querySelector('media-gallery');
    const slides = [...(gallery?.querySelectorAll('slideshow-slide') || [])];
    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${mediaId}"]`));
    if (index > -1 && gallery?.slideshow?.select) gallery.slideshow.select(index, undefined, { animate: true });
  }

  preloadZoomImage(src) {
    if (!src) return Promise.resolve(null);
    const cached = this.zoomImageCache?.get(src);
    if (cached) return cached;

    const promise = new Promise((resolve) => {
      const preload = new Image();
      preload.decoding = 'async';
      preload.onload = async () => {
        try { await preload.decode?.(); } catch (_) {}
        resolve(src);
      };
      preload.onerror = () => resolve(null);
      preload.src = src;
      if (preload.complete && preload.naturalWidth > 0) {
        try {
          const decoded = preload.decode?.();
          if (decoded?.then) decoded.then(() => resolve(src)).catch(() => resolve(src));
          else resolve(src);
        } catch (_) { resolve(src); }
      }
    });

    this.zoomImageCache?.set(src, promise);
    return promise;
  }

  async setZoomImage(image, src, alt, token) {
    if (!image || !src || token !== this.zoomRequestToken) return false;
    image.classList.remove('is-ready');
    image.alt = alt || '';
    image.removeAttribute('src');
    image.srcset = '';
    image.src = src;

    try {
      if (image.decode) await image.decode();
      else if (!image.complete) await new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });
    } catch (_) {}

    if (token !== this.zoomRequestToken || image.src !== new URL(src, document.baseURI).href) return false;
    image.classList.add('is-ready');
    return true;
  }

  async openZoom(button) {
    const preview = button?.closest('[data-rfb-preview]');
    const dialog = this.querySelector('[data-rfb-zoom-dialog]');
    const image = dialog?.querySelector('[data-rfb-zoom-image]');
    const thumb = preview?.querySelector('img');
    const hdSrc = preview?.dataset.rfbZoomSrc || thumb?.currentSrc || thumb?.src;
    const placeholderSrc = thumb?.currentSrc || thumb?.src || hdSrc;
    const alt = preview?.dataset.rfbZoomAlt || thumb?.alt || '';
    if (!dialog || !image || !hdSrc) return;

    const token = ++this.zoomRequestToken;
    dialog.classList.add('is-loading');
    image.classList.remove('is-ready');
    image.alt = alt;
    image.removeAttribute('src');
    image.srcset = '';

    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('rfb-zoom-open');

    // Paint only the CURRENT thumbnail while the HD source is decoding. The previous
    // zoom image is cleared first, so it can never flash during a fast variant switch.
    if (placeholderSrc && placeholderSrc !== hdSrc) {
      await this.setZoomImage(image, placeholderSrc, alt, token);
      if (token !== this.zoomRequestToken || !dialog.open) return;
    }

    const readyHd = await this.preloadZoomImage(hdSrc);
    if (token !== this.zoomRequestToken || !dialog.open) return;

    if (readyHd) await this.setZoomImage(image, hdSrc, alt, token);
    else if (!image.getAttribute('src')) await this.setZoomImage(image, placeholderSrc, alt, token);

    if (token === this.zoomRequestToken && dialog.open) dialog.classList.remove('is-loading');
  }

  closeZoom() {
    const dialog = this.querySelector('[data-rfb-zoom-dialog]');
    if (!dialog) return;
    this.zoomRequestToken += 1;
    dialog.classList.remove('is-loading');
    if (dialog.open) dialog.close();
    document.documentElement.classList.remove('rfb-zoom-open');
  }

  openGuide() {
    const dialog = this.querySelector('[data-rfb-guide]');
    if (!dialog) return;
    dialog.showModal();
    document.documentElement.classList.add('rfb-guide-open');
  }

  closeGuide() {
    this.querySelector('[data-rfb-guide]')?.close();
    document.documentElement.classList.remove('rfb-guide-open');
  }

  startTimer() {
    const clock = this.querySelector('[data-rfb-clock]');
    if (!clock) return;
    const key = `rfb:${this.dataset.productId}`;
    const duration = Number(this.dataset.timerMinutes || 15) * 60000;
    let end = Number(sessionStorage.getItem(key));
    if (!end || end < Date.now()) {
      end = Date.now() + duration;
      sessionStorage.setItem(key, end);
    }
    const tick = () => {
      const left = Math.max(0, end - Date.now());
      clock.textContent = `${String(Math.floor(left / 60000)).padStart(2, '0')}:${String(Math.floor(left / 1000) % 60).padStart(2, '0')}`;
    };
    tick();
    this.timer = setInterval(tick, 1000);
  }

  startStock() {
    const value = this.querySelector('[data-rfb-stock]');
    if (!value) return;
    const start = Number(value.textContent);
    const initialFill = Math.max(1, Math.min(20, Number(this.dataset.stockInitialFill) || 5));
    let stock = start;
    const render = () => {
      value.textContent = stock;
      // Scarcity bar: the starting count intentionally occupies only a small fraction
      // of the track (5% by default) and then decreases with the counter.
      const ratio = Math.max(0.8, Math.min(initialFill, initialFill * (stock / Math.max(start, 1))));
      const bar = this.querySelector('[data-rfb-bar]');
      if (bar) bar.style.width = `${ratio}%`;
    };
    render();
    this.stockTimer = setInterval(() => {
      if (stock > 3) {
        stock -= 1;
        render();
      }
    }, 19000);
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy) return;

    const ids = this.units.map((unit) => Number(unit.querySelector('[data-rfb-variant-id]')?.value));
    const error = this.querySelector('[data-rfb-error]');
    if (ids.some((id) => !id)) {
      if (error) error.hidden = false;
      return;
    }

    if (error) error.hidden = true;
    const requestKey = `rfb:${window.location.pathname}:${ids.join(',')}`;
    const activeRequests = window.__ritualCartRequests || (window.__ritualCartRequests = new Set());
    if (activeRequests.has(requestKey)) return;
    activeRequests.add(requestKey);
    this.busy = true;
    const button = this.querySelector('[data-rfb-submit]');
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const groupId = String(Date.now());
    const selections = ids.map((id, index) => ({
      id,
      quantity: 1,
      properties: {
        '_Ritual fashion offer': index === 0 ? 'Paid item' : `Complimentary item ${index}`,
        '_Ritual offer group': groupId,
      },
    }));

    // Shopify's native Buy X Get Y engine decides which eligible cart lines receive
    // the Y discount server-side. When X and Y are variants of the same product and
    // have identical prices, cart-line order can decide which otherwise-equivalent
    // lines are discounted. Keep the shopper-facing first selection as the paid item
    // by adding complimentary selections first and the paid selection last. Private
    // role/group properties also prevent equal variants from collapsing into one line.
    // Reverse the logical paid-then-gifts order for the AJAX payload. Shopify places
    // newly-added lines at the top of the cart in reverse insertion order, so shoppers
    // still see Paid item -> Gift 1 -> Gift 2 while the paid item reaches BXGY last.
    const items = [...selections].reverse();

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: ['cart-drawer-section'] }),
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      this.refreshDrawer(data.sections?.['cart-drawer-section']);
      await this.refreshBubble();
      if (this.dataset.skipCart === 'true') {
        window.location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
        return;
      }
      if (this.dataset.openCart === 'true') {
        requestAnimationFrame(() => document.querySelector('[data-testid="cart-drawer-trigger"]')?.click());
      }
    } catch (exception) {
      console.error('[Ritual fashion BOGO]', exception);
      if (error) error.hidden = false;
    } finally {
      activeRequests.delete(requestKey);
      this.busy = false;
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  refreshDrawer(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('#shopify-section-cart-drawer-section');
    const current = document.querySelector('#shopify-section-cart-drawer-section');
    if (fresh && current) current.replaceWith(fresh);
  }

  async refreshBubble() {
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((response) => response.json());
    document.querySelectorAll('[data-testid="cart-bubble"]').forEach((bubble) => {
      bubble.textContent = cart.item_count;
      bubble.classList.toggle('hidden', cart.item_count === 0);
      bubble.closest('.cart-bubble')?.classList.toggle('visually-hidden', cart.item_count === 0);
    });
  }

  setupScrollCues() {
    this.scrollCueItems = [...this.querySelectorAll('.rfb__values-wrap')];
    this.updateScrollCues = () => {
      this.scrollCueItems.forEach((wrap) => {
        const scroller = wrap.querySelector('.rfb__values');
        if (!scroller) return;
        const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        const position = Math.abs(scroller.scrollLeft);
        wrap.classList.toggle('has-overflow', max > 6);
        wrap.classList.toggle('is-at-end', max <= 6 || position >= max - 6);
      });
    };

    this.scrollCueItems.forEach((wrap) => {
      const scroller = wrap.querySelector('.rfb__values');
      if (!scroller) return;
      scroller.addEventListener('scroll', this.updateScrollCues, { passive: true });
    });
    window.addEventListener('resize', this.updateScrollCues, { passive: true });
    requestAnimationFrame(this.updateScrollCues);
  }
}

if (!customElements.get('ritual-fashion-bogo')) customElements.define('ritual-fashion-bogo', RitualFashionBogo);
